/**
 * Service de journalisation d'audit pour les tenants
 * 
 * Ce service permet d'enregistrer toutes les actions effectuées par les utilisateurs
 * dans un contexte multi-tenant, en assurant que les journaux d'audit sont correctement
 * isolés par tenant.
 */

import tenantManager from './tenant-manager';

// Types d'actions possibles dans le système
export enum AuditActionType {
  CREATE = 'create',
  READ = 'read',
  UPDATE = 'update',
  DELETE = 'delete',
  LOGIN = 'login',
  LOGOUT = 'logout',
  SWITCH_TENANT = 'switch_tenant',
  EXPORT = 'export',
  IMPORT = 'import',
  SHARE = 'share',
  CONFIGURE = 'configure',
  SCAN = 'scan',
  REMEDIATE = 'remediate',
  INVITE = 'invite' // Ajout de l'action d'invitation
}

// Interface pour les entrées d'audit
export interface AuditEntry {
  tenantId: string;
  userId: string;
  action: AuditActionType | string;
  resource: string;
  resourceId?: string;
  timestamp: Date;
  ipAddress?: string;
  userAgent?: string;
  details?: any;
  status: 'success' | 'failure';
  errorMessage?: string;
}

export class TenantAuditService {
  private auditLogs: Map<string, AuditEntry[]> = new Map();
  
  /**
   * Enregistre une action d'audit pour un tenant spécifique
   */
  async logAction(entry: Omit<AuditEntry, 'timestamp'>): Promise<void> {
    // Vérifier l'accès au tenant
    if (!tenantManager.isTenantAccessible(entry.tenantId)) {
      console.warn(`Tentative de journalisation pour un tenant inaccessible: ${entry.tenantId}`);
      return;
    }
    
    // Créer l'entrée d'audit complète
    const auditEntry: AuditEntry = {
      ...entry,
      timestamp: new Date()
    };
    
    // Ajouter l'entrée aux logs en mémoire
    if (!this.auditLogs.has(entry.tenantId)) {
      this.auditLogs.set(entry.tenantId, []);
    }
    this.auditLogs.get(entry.tenantId)!.push(auditEntry);
    
    // En production, envoyer l'entrée au backend pour stockage persistant
    try {
      await this.persistAuditEntry(auditEntry);
      console.log(`[Audit] Action ${entry.action} enregistrée pour le tenant ${entry.tenantId}`);
    } catch (error) {
      console.error(`Erreur lors de la persistance de l'entrée d'audit:`, error);
    }
    
    // Si l'action est critique, créer également une notification
    if (this.isCriticalAction(entry.action) || entry.status === 'failure') {
      await this.createAuditNotification(auditEntry);
    }
  }
  
  /**
   * Récupère les entrées d'audit pour un tenant spécifique
   */
  async getAuditLogs(tenantId: string, filters?: {
    userId?: string;
    action?: AuditActionType | string;
    resource?: string;
    startDate?: Date;
    endDate?: Date;
    status?: 'success' | 'failure';
  }): Promise<AuditEntry[]> {
    // Vérifier l'accès au tenant
    if (!tenantManager.isTenantAccessible(tenantId)) {
      throw new Error(`Accès non autorisé aux journaux d'audit du tenant: ${tenantId}`);
    }
    
    // En production, récupérer les logs depuis le backend
    // Pour cette démonstration, nous utilisons les logs en mémoire
    let logs = this.auditLogs.get(tenantId) || [];
    
    // Appliquer les filtres si spécifiés
    if (filters) {
      if (filters.userId) {
        logs = logs.filter(log => log.userId === filters.userId);
      }
      if (filters.action) {
        logs = logs.filter(log => log.action === filters.action);
      }
      if (filters.resource) {
        logs = logs.filter(log => log.resource === filters.resource);
      }
      if (
        filters.startDate instanceof Date &&
        !isNaN(filters.startDate.getTime())
      ) {
        const startDate = filters.startDate;
        logs = logs.filter(log => log.timestamp >= startDate);
      }
      if (
        filters.endDate instanceof Date &&
        !isNaN(filters.endDate.getTime())
      ) {
        const endDate = filters.endDate;
        logs = logs.filter(log => log.timestamp <= endDate);
      }
      if (filters.status) {
        logs = logs.filter(log => log.status === filters.status);
      }
    }
    
    // Trier par date décroissante (plus récent en premier)
    return logs.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
  }
  
  /**
   * Détermine si une action est considérée comme critique
   * et nécessite une notification
   */
  private isCriticalAction(action: AuditActionType | string): boolean {
    const criticalActions = [
      AuditActionType.DELETE,
      AuditActionType.CONFIGURE,
      AuditActionType.SHARE,
      AuditActionType.IMPORT,
      AuditActionType.EXPORT,
      AuditActionType.REMEDIATE
    ];
    
    return criticalActions.includes(action as AuditActionType);
  }
  
  /**
   * Persiste une entrée d'audit dans le backend
   */
  private async persistAuditEntry(entry: AuditEntry): Promise<void> {
    // En production, envoyer au backend via une API
    // Pour cette démonstration, nous simulons un appel API
    
    // Simulation d'un délai réseau
    await new Promise(resolve => setTimeout(resolve, 50));
    
    // Stocker dans le localStorage pour la démonstration
    try {
      const storageKey = `audit_logs_${entry.tenantId}`;
      const existingLogs = JSON.parse(localStorage.getItem(storageKey) || '[]');
      existingLogs.push(entry);
      localStorage.setItem(storageKey, JSON.stringify(existingLogs));
    } catch (error) {
      console.warn('Impossible de stocker les logs d\'audit dans localStorage:', error);
    }
  }
  
  /**
   * Crée une notification pour une entrée d'audit critique
   */
  private async createAuditNotification(entry: AuditEntry): Promise<void> {
    // En production, utiliser le NotificationService
    // Pour cette démonstration, nous simulons la création d'une notification
    
    const priority = entry.status === 'failure' ? 'high' : 
                    this.isCriticalAction(entry.action) ? 'medium' : 'low';
    
    const notification = {
      tenantId: entry.tenantId,
      priority,
      title: `Action ${entry.action} ${entry.status === 'success' ? 'réussie' : 'échouée'}`,
      message: `L'utilisateur ${entry.userId} a ${entry.action === AuditActionType.DELETE ? 'supprimé' : 'modifié'} ${entry.resource}${entry.resourceId ? ` (${entry.resourceId})` : ''}`,
      contentType: 'audit_log',
      objectId: entry.resourceId || undefined,
      metadata: entry.details,
      createdAt: entry.timestamp,
      expiresAt: new Date(entry.timestamp.getTime() + 30 * 24 * 60 * 60 * 1000), // 30 jours
      read: false
    };
    
    console.log(`[Audit] Notification créée:`, notification);
    
    // En production, envoyer au service de notification
    // notificationService.createNotification(notification);
  }
}

// Exporter une instance unique du service
export const tenantAuditService = new TenantAuditService();

export default tenantAuditService;
